import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { GraphQLModule, PlusAuthenticationService } from '@karve.it/core';
import { Store } from '@ngrx/store';
import { QueryRef } from 'apollo-angular';


import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { SubSink } from 'subsink';

import { environment } from '../../environments/environment';
import { CurrentBrandingGQL, CurrentBrandingQuery, CurrentBrandingQueryVariables, CurrentZoneGQL, CurrentZoneQuery, CurrentZoneQueryVariables, ZoneDir } from '../../generated/graphql.generated';
import { ColorSchemeService } from '../color-scheme.service';
import { ColorScheme } from '../colors';
import { ZonesActions } from '../core/zones/zones.actions';
import { BrandingIconSize, defaultEventTypeInfoMap, defaultEventTypeInfoMapV2, eventTypeInfoMap, eventTypeInfoMapV2 } from '../global.constants';

import { generateListAvailableProductsQueryVariables } from '../job/job-charges-helpers';

import { BrandingActions } from '../state/branding.store';
import { selectFeature } from '../state/featureFlags.store';

import { AppLoadingService } from './app-loading.service';
import { overrideEventTypesFromConfigs } from './event-type.util';
import { FeatureName, features } from './features';
import { brandingConfigKeys } from './frontend-configs';
import { PermissionService } from './permission.service';

/**
 * Handles the current context, including
 * application branding and information about the currently authenticated
 * zones.
 *
 * **currentBranding**: A behaviour subject containing the applications current ZoneBranding
 * Updated any time the zone changes.
 *
 * **currentZone**: A behaviour subject containing the applications current zone. Updated
 * any time the zone changes
 */
@Injectable({
  providedIn: 'root'
})
export class BrandingService implements OnDestroy {

  subs = new SubSink();

  /**
   * The current (or last) branding - always defined, may be inaccurate if we
   * are loading the next zone it will still have the old zone loaded
   */
  public currentBranding = new BehaviorSubject<CurrentBrandingQuery['branding']>(environment.defaultBranding);

  public loading = new BehaviorSubject<boolean>(true);

  private currentZone$ = new BehaviorSubject<CurrentZoneQuery['currentZone']>(undefined);

  private favicon: HTMLLinkElement = document.querySelector('#appIcon');

  private currentZoneQuery: QueryRef<CurrentZoneQuery, CurrentZoneQueryVariables>;

  private brandingReloadSubject = new BehaviorSubject<'reload' | undefined>(undefined);

  private resetEventTypeMapsToDefaults() {
    overrideEventTypesFromConfigs(eventTypeInfoMap, null, defaultEventTypeInfoMap);
    overrideEventTypesFromConfigs(eventTypeInfoMapV2, null, defaultEventTypeInfoMapV2);
  }

  constructor(
    private plusAuth: PlusAuthenticationService,
    private brandingGQL: CurrentBrandingGQL,
    private colorSchemeService: ColorSchemeService,
    private graphqlModule: GraphQLModule,
    private appLoadingService: AppLoadingService,
    private currentZoneGQL: CurrentZoneGQL,
    private permissionSvc: PermissionService,
    private router: Router,
    private store: Store,
    @Inject(DOCUMENT) private doc: Document
  ) {
    this.initializeBrandingQuery();
  }

  private initializeBrandingQuery() {
    this.subs.sink = this.brandingReloadSubject.pipe(
      filter((value) => value === 'reload'), // Do not fire on default value (undefined)
      switchMap(() => {
        this.store.dispatch(BrandingActions.brandingLoading({
          loading: true,
        }));
        return this.brandingGQL.fetch(this.getVariables(), {errorPolicy: 'all'});
      })
    ).subscribe({
      next: (res) => {
        this.appLoadingService.error = undefined;
        this.loading.next(res.loading);

        if (res.loading) {
          this.store.dispatch(BrandingActions.brandingLoading({
            loading: res.loading,
          }));
          return;
        }

        if (!res.data?.branding) {
          this.resetEventTypeMapsToDefaults();
          this.store.dispatch(BrandingActions.brandingLoading({
            loading: false,
          }));
          return;
        }


        const vars = this.getVariables();
        const matchesZone = vars.zoneId === res?.data?.branding?.zoneId;

        // This check can be misleading because if we change zones while branding is loading,
        // there may be a mismatch. The `zoneId` in `vars` represents the new zone, but the fetched branding
        // corresponds to the old `zoneId`. However, since we are using `switchMap`, the latest branding
        // for the new `zoneId` will eventually be fetched and updated correctly.
        if (!matchesZone) {
            console.warn(`Branding zoneId does not match current zoneId`, vars.zoneId, res.data.branding.zoneId);
            this.resetEventTypeMapsToDefaults();
            this.store.dispatch(BrandingActions.brandingLoading({
                loading: false,
            }));
            return;
        }

        const zoneEventTypes = res.data.getZoneConfigValues?.CALENDAREVENTS_TYPES;

        // Requirement: keep maps updated before currentBranding.next(), because subscribers are synchronous.
        overrideEventTypesFromConfigs(eventTypeInfoMap, zoneEventTypes, defaultEventTypeInfoMap);
        overrideEventTypesFromConfigs(eventTypeInfoMapV2, zoneEventTypes, defaultEventTypeInfoMapV2);

        // this.setFavicon(res.data.branding.ico);
        this.currentBranding.next(res.data.branding);

        // sets loading
        this.store.dispatch(BrandingActions.brandingUpdated({
          features: res.data.features || [],
          branding: res.data.branding,
          configValues: res.data.getZoneConfigValues,
          commentModes: res.data.commentModes,
          permissions: res.data.permissions.permissions,
          products: res?.data?.products?.products || [],
          templates: res?.data?.templates?.templates || [],
          taxes: res?.data?.taxes?.taxes || [],
          expenses: res?.data?.expenses?.expenses || [],
        }));

        const permissions = res.data.permissions.permissions;
        this.permissionSvc.updatePermissions(permissions);
      },
      error: (err) => {
        console.error(`Error retrieving branding`, err);
        this.resetEventTypeMapsToDefaults();
        this.currentBranding.next(environment.defaultBranding);
        this.permissionSvc.updatePermissions(undefined);
        this.loading.next(false);

        this.store.dispatch(BrandingActions.brandingLoadError({
          error: err,
        }));
        this.appLoadingService.error = err;
      }
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /**
   * Reload or initialize the branding for the current context in an async way
   */
  reloadBranding() {
    if (this.plusAuth.isDeauthenticating() || this.plusAuth.isDeauthed()) {
      return;
    }

    this.brandingReloadSubject.next('reload');
  }

  reloadCurrentZone() {
    if (!this.currentZoneQuery) { return; }
    this.currentZoneQuery.refetch();
  }

  watchCurrentZone(){
    if (this.currentZone$?.value){
      console.error(`Current Zone already defined`);
    }

    this.currentZoneQuery = this.currentZoneGQL.watch({});

    this.subs.sink = this.currentZoneQuery.valueChanges.subscribe((res) => {
      // Loading prevents recieving duplicates from the cache and stale prevents recieving out of date values of context change
      if (res.loading) {
        return;
      }

      if (res.data?.currentZone) {
        console.log('Current Zone: ', res.data.currentZone.id);
        this.setCurrentZone(res.data.currentZone);
        // Get the branding for the current zone after it is set
        this.reloadBranding();
      }
    });
  }

  /**
   * Do not call .next on this value!!!
   * This will break the update flow
   */
  currentZone(){
    return this.currentZone$;
  }

  setCurrentZone(currentZone: CurrentZoneQuery['currentZone']) {
    this.store.dispatch(ZonesActions.zoneChanged({ currentAppZone: currentZone }));
    if (this.currentZone().value?.id !== currentZone.id) {
      this.currentZone$.next(currentZone);

      // This will check that we are on the right url for this zone and move us if we are not
      // if (this.doc.location.host !== currentZone.domain && currentZone?.domain && isDevMode() === false){
        // this.doc.location.href = `https://${currentZone.domain}/`;
      // }
    }
  }

  setFavicon(str: string) {
    const url = new URL(str, str.startsWith('http') ? '' : location.origin);
    this.favicon.href = url.href;
  }

  /**
   * Return an observable which contains the most up to date path to the
   * icon at a specific size for the application.
   *
   * This observable updates when the zone changes and when the branding changes
   * and will return immediately after you subscribe. If the icon does not change
   * following a zone or scheme (dark/light) change then this will not fire a subject.
   * IE only distinct icon paths will be returned.
   *
   * HTML Example:
   *
   * ```
   *  <img
   *    [src]="brandingService.watchIcon(256) | async"
   *    alt="Logo"
   *  />
   * ```
   *
   * @param desiredSize The requested icon size returns an image of that size.
   * Can be 48, 64, 128, 196, 256, 512, 1024
   *
   * @param forceScheme Force light or dark scheme
   * @returns And observable which you can subscribe to to receive the latest icon path
   */
  watchIcon(
    desiredSize: BrandingIconSize,
    forceScheme?: ColorScheme,
  ) {

    return combineLatest([
      this.colorSchemeService.colorScheme,
      this.currentBranding,
    ]).pipe(
      map(([ scheme, branding ]) => {

        // TODO: if we give it a weird size then try to find the next highest size
        if (forceScheme) {
          scheme = forceScheme;
        }

        const key = `icon${ scheme === 'dark' ? 'Dark' : 'Light' }${ desiredSize }`;
        return branding[key];
      }),
      distinctUntilChanged(),
    );
  }

  getVariables(): CurrentBrandingQueryVariables {
    let zoneId = localStorage.getItem(environment.lskeys.lastBrandingZone);
    // if (this.auth.user?.zone) {
    //   zoneId = this.auth.user.zone;
    //   localStorage.setItem(environment.lskeys.lastBrandingZone, zoneId);
    // }

    if (!zoneId) {
      zoneId = undefined;
    }

    if (!zoneId && this.graphqlModule.zone.value) {
      zoneId = this.graphqlModule.zone.value;
    }

    const { listProductQueryVariables } = generateListAvailableProductsQueryVariables(zoneId);


    return {
      zoneId,
      features: features as unknown as string[],
      configKeys: brandingConfigKeys,
      templatesFilter: {
        zoneDir: ZoneDir.Gte,
      },
      ...listProductQueryVariables,
    };
  }

  watchFeature(feature: FeatureName) {
    return this.store.select(selectFeature(feature));
  }
}
